// src/utils/date.js
// Shared date formatting and calendar helper utilities

/**
 * Parse an ISO date string safely using local date parts (no timezone shift).
 * e.g. "2025-12-25" → Date representing 25 Dec 2025 at midnight local time.
 */
export function parseDate(isoString) {
  if (!isoString) return new Date();
  const [datePart] = isoString.split('T');
  const [year, month, day] = datePart.split('-');
  return new Date(year, month - 1, day);
}

/** Returns the day-of-month number from an ISO date string. */
export function getDayNumber(isoString) {
  return isoString ? parseDate(isoString).getDate() : '';
}

/** Returns the abbreviated month name from an ISO date string. */
export function getMonthName(isoString) {
  return isoString ? parseDate(isoString).toLocaleDateString([], { month: 'short' }) : '';
}

/** Returns "HH:MM" time string from an ISO date/datetime string. */
export function getTimeString(isoString) {
  if (!isoString) return '';
  return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** Returns a long-form date string, e.g. "Wednesday, 25 December 2025". */
export function formatFullDate(isoString) {
  if (!isoString) return '';
  return parseDate(isoString).toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Returns a human-readable countdown string from now until an event.
 * e.g. "2 days, 3 hrs" or "45 minutes".
 */
export function getTimeRemaining(isoString, isAllDay, now = new Date()) {
  if (!isoString) return '';
  const target = new Date(isoString);

  if (isAllDay) {
    const [datePart] = isoString.split('T');
    const [year, month, day] = datePart.split('-');
    target.setFullYear(year, month - 1, day);
    target.setHours(0, 0, 0, 0);
  }

  const diffMs = target - now;
  if (diffMs <= 0) return 'Today / In progress';

  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    const remainingHours = diffHours % 24;
    return `${diffDays} day${diffDays > 1 ? 's' : ''}${remainingHours > 0 ? `, ${remainingHours} hr${remainingHours > 1 ? 's' : ''}` : ''}`;
  }

  if (diffHours > 0) {
    const remainingMins = diffMinutes % 60;
    return `${diffHours} hr${diffHours > 1 ? 's' : ''}${remainingMins > 0 ? `, ${remainingMins} min` : ''}`;
  }

  return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''}`;
}

/**
 * Returns 'TODAY', 'TOMORROW', or null based on the event date relative to now.
 */
export function getRelativeDateLabel(isoString, now = new Date()) {
  if (!isoString) return null;
  const eventDate = parseDate(isoString);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (eventDate.getTime() === today.getTime()) return 'TODAY';
  if (eventDate.getTime() === tomorrow.getTime()) return 'TOMORROW';
  return null;
}

/**
 * Returns the CSS class for the date badge based on relative label.
 */
export function getDateTagClass(isoString, now = new Date()) {
  const label = getRelativeDateLabel(isoString, now);
  if (label === 'TODAY') return 'is-today';
  if (label === 'TOMORROW') return 'is-tomorrow';
  return '';
}

/** Format a date string to a short locale date, e.g. "25 Aug 2025". */
export function formatDate(d) {
  if (!d) return 'Never';
  const date = new Date(d);
  return isNaN(date.getTime())
    ? d
    : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Check if a date string represents today. */
export function isToday(dateString) {
  if (!dateString) return false;
  return dateString.slice(0, 10) === new Date().toISOString().slice(0, 10);
}

/** Get initials from a full name string. */
export function getInitials(name) {
  if (!name) return 'U';
  const parts = name.trim().split(' ');
  return parts.length >= 2
    ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    : name.slice(0, 2).toUpperCase();
}
